import React, { useEffect, useState } from "react";
import { Row, Col } from "reactstrap";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";

import { TextInput } from "../../../components/Input";
import { CustomButton } from "../../../components/CustomButton";
import { FormikErrorText } from "../../../components/FormikErrorText";
import { AutoComplete } from "../../../components/AutoComplete";
import RadioGroup from "../../../components/Input/RadioGroup";
import { customStyles } from "../../UserManagement/CustomStyles";
import { PLACEHOLDERS, LABELS, NAMES } from "../Config";
import { useRequestProcessor } from "../../../hooks/useRequestProcessors";
import { getKey, MODULE_KEYS } from "../../../api/api.keys";
import { GetAudiosSubCategories, GetAuthor, GetNarrator } from "../../../api/api.service";
import UploadContainer from "../UploadContainer";
import SubscriptionStatus from "../../../constants/SubscriptionStatus";
import { mapToOptions, truncateString } from "../../../utils/commonUtils";

import { SOUNDSCAPE_AUDIO_FORM_VALIDATION_SCHEMA } from "../FormValidation";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { FileUploader } from "../../../components/FileUploader";
import { MediaType } from "../../../constants";
import { USER_STATUS } from "../../../constants/UserStatus";

const EDIT = "edit";
const CHARACTER_LIMIT = 30;

let {
    TITLE,
    SUB_CATEGORY,
    DESCRIPTION,
    AUTHOR,
    SESSION_SUCCESS_MESSAGE,
    RADIO_GROUP,
    UPLOAD_AUDIO,
    UPLOAD_THUMBNAIL,
    UPLOAD_IMAGE
} = LABELS;

const AudioSoundscapeForm = ({ mode, isLoading, data, onUpdateAudio, onAddAudio, isDisabled }) => {
    let isAudioEditable = mode === EDIT;
    const history = useHistory();
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreSubCategories, setHasMoreSubCategories] = useState(true);
    const [hasMoreAuthors, setHasMoreAuthors] = useState(true);
    const [hasMoreNarrators, setHasMoreNarrators] = useState(true);
    const [subCategoryOptions, setSubCategoryOptions] = useState([]);
    const [authorOptions, setAuthorOptions] = useState([]);
    const [narratorOptions, setNarratorOptions] = useState([]);

    const updatedInitialValues = {
        [NAMES[TITLE]]: data?.title,
        [NAMES[SUB_CATEGORY]]: isAudioEditable
            ? data?.albumLabels?.map((item) => ({
                  value: item.label?.id,
                  label: item.label?.title
              })) || []
            : null,

        [NAMES[DESCRIPTION]]: data?.description,
        [NAMES[AUTHOR]]: isAudioEditable
            ? { value: data?.author?.id, label: data?.author?.name }
            : null,

        [NAMES[UPLOAD_AUDIO]]: data?.audios[0]?.audioMedia || null,
        [NAMES[UPLOAD_THUMBNAIL]]: data?.croppedThumbnail || null,
        [NAMES[UPLOAD_IMAGE]]: data?.thumbnail || null,
        [NAMES[RADIO_GROUP]]: SubscriptionStatus.getDisplayTextKey(data?.tierType),
        [NAMES[SESSION_SUCCESS_MESSAGE]]: data?.audios[0]?.completionMessage
    };
    const { useFetch } = useRequestProcessor();

    const formik = useFormik({
        initialValues: updatedInitialValues,
        validationSchema: SOUNDSCAPE_AUDIO_FORM_VALIDATION_SCHEMA,
        onSubmit: (values) => {
            if (isAudioEditable) {
                onUpdateAudio(values);
            } else {
                onAddAudio(values);
            }
        }
    });

    const { data: subCategories, isFetching: isFetchingSubCategories } = useFetch(
        getKey([MODULE_KEYS.AUDIOS_SUB_CATEGORY, currentPage], {}),
        async ({ signal }) =>
            await GetAudiosSubCategories({
                params: { page: currentPage, limit: 10 },
                signal
            }),
        { keepPreviousData: true, enabled: hasMoreSubCategories, staleTime: 0, cacheTime: 0 }
    );

    const { data: authors, isFetching: isFetchingAuthors } = useFetch(
        getKey([MODULE_KEYS.AUTHOR, currentPage], {}),
        async ({ signal }) =>
            await GetAuthor({
                params: { page: currentPage, limit: 10, status: USER_STATUS.ACTIVE },
                signal
            }),
        { keepPreviousData: true, enabled: hasMoreAuthors, staleTime: 0, cacheTime: 0 }
    );

    const { data: narrators, isFetching: isFetchingNarrators } = useFetch(
        getKey([MODULE_KEYS.NARRATOR, currentPage], {}),
        async ({ signal }) =>
            await GetNarrator({
                params: { page: currentPage, limit: 10, status: USER_STATUS.ACTIVE },
                signal
            }),
        { keepPreviousData: true, enabled: hasMoreNarrators, staleTime: 0, cacheTime: 0 }
    );

    useEffect(() => {
        if (authors?.data) {
            const newOptions = mapToOptions(authors.data, "name", "id").map((option) => ({
                ...option,
                label: truncateString(option.label, CHARACTER_LIMIT)
            }));
            setAuthorOptions((prev) => [...prev, ...newOptions]);
            if (authorOptions.length + newOptions.length >= authors.total) {
                setHasMoreAuthors(false);
            }
        }
    }, [authors]);

    useEffect(() => {
        if (narrators?.data) {
            const newOptions = mapToOptions(narrators.data, "name", "id").map((option) => ({
                ...option,
                label: truncateString(option.label, CHARACTER_LIMIT)
            }));

            setNarratorOptions((prev) => [...prev, ...newOptions]);
            if (narratorOptions.length + newOptions.length >= narrators.total) {
                setHasMoreNarrators(false);
            }
        }
    }, [narrators]);

    useEffect(() => {
        if (subCategories?.data) {
            const newOptions = mapToOptions(subCategories.data, "title", "id");
            setSubCategoryOptions((prev) => [...prev, ...newOptions]);
            if (subCategoryOptions.length + newOptions.length >= subCategories.total) {
                setHasMoreSubCategories(false);
            }
        }
    }, [subCategories]);

    const handleScroll = (e, type) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 20) {
            if (type === "subCategory" && hasMoreSubCategories && !isFetchingSubCategories) {
                setCurrentPage((prev) => prev + 1);
            }
            if (type === "author" && hasMoreAuthors && !isFetchingAuthors) {
                setCurrentPage((prev) => prev + 1);
            }
            if (type === "narrator" && hasMoreNarrators && !isFetchingNarrators) {
                setCurrentPage((prev) => prev + 1);
            }
        }
    };

    const handleFileUpload = async (file, exactFile, fieldName) => {
        formik.setFieldValue(NAMES[fieldName], { file, exactFile }); // Store file and mediaId in form
    };
    const handleFileRemove = (fieldName) => {
        formik.setFieldValue(NAMES[fieldName], null); // Clear file from form
    };

    const handleCancel = () => {
        if (isLoading) return;
        formik.resetForm();
        history.push(APP_ROUTES.AUDIO_CONTENT_MANAGEMENT_SOUNDSCAPES);
    };

    return (
        <form onSubmit={formik.handleSubmit} className="p-4">
            <Row>
                <Col md={6}>
                    <TextInput
                        placeholder={PLACEHOLDERS[TITLE]}
                        label={TITLE}
                        name={NAMES[TITLE]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[NAMES[TITLE]]}
                        className="hide-default-icon"
                        disabled={isDisabled}
                        autoFocus
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[TITLE]} />
                </Col>

                <Col md={6}>
                    <AutoComplete
                        isDisabled={isDisabled}
                        classNamePrefix="status-header-search-admin"
                        isLoading={isFetchingSubCategories}
                        handleScroll={(e) => handleScroll(e, "subCategory")}
                        label={SUB_CATEGORY}
                        value={formik.values[NAMES[SUB_CATEGORY]] || []}
                        onChange={(option) =>
                            formik.setFieldValue(NAMES[SUB_CATEGORY], option || null)
                        }
                        onBlur={() => formik.setFieldTouched(NAMES[SUB_CATEGORY])}
                        placeholder={PLACEHOLDERS[SUB_CATEGORY]}
                        options={subCategoryOptions}
                        maxMenuHeight={220}
                        customStyles={customStyles.filter}
                        isMulti={true}
                        isClearable
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[SUB_CATEGORY]} />
                </Col>
            </Row>

            <Row className="my-4">
                <TextInput
                    placeholder={PLACEHOLDERS[DESCRIPTION]}
                    label={DESCRIPTION}
                    name={NAMES[DESCRIPTION]}
                    type="textarea"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values[NAMES[DESCRIPTION]]}
                    className="hide-default-icon"
                    disabled={isDisabled}
                />
                <FormikErrorText formikInstance={formik} fieldName={NAMES[DESCRIPTION]} />
            </Row>

            {/* Author & Narrator */}
            <Row className="my-4">
                <Col md={6}>
                    <AutoComplete
                        isDisabled={isDisabled}
                        classNamePrefix="status-header-search-admin"
                        isLoading={isFetchingAuthors}
                        handleScroll={(e) => handleScroll(e, "author")}
                        label={AUTHOR}
                        value={
                            isAudioEditable
                                ? authorOptions.find(
                                      (option) =>
                                          option.value === formik.values[NAMES[AUTHOR]]?.value
                                  )
                                : formik.values[NAMES[AUTHOR]]
                        }
                        onChange={(option) => formik.setFieldValue(NAMES[AUTHOR], option || null)}
                        onBlur={() => formik.setFieldTouched(NAMES[AUTHOR])}
                        placeholder={PLACEHOLDERS[AUTHOR]}
                        options={authorOptions}
                        maxMenuHeight={220}
                        customStyles={customStyles.filter}
                        isClearable
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[AUTHOR]} />
                </Col>
            </Row>

            <UploadContainer
                formik={formik}
                setFieldValue={formik.setFieldValue}
                formValues={formik.values}
                data={data && data}
            />

            <Row className="my-4">
                <Col md={12}>
                    <p className="mb-2  input-label">{UPLOAD_IMAGE}</p>
                    <FileUploader
                        fileType={MediaType.IMAGE}
                        supportedFormats="Supported format: .jpg, .png Max file size: 25MB"
                        acceptedFileTypes={["jpg", "png"]} // Define accepted types
                        maxFileSize={1} // Limit size to 1MB
                        onFileUpload={(file, exactFile) =>
                            handleFileUpload(file, exactFile, UPLOAD_IMAGE)
                        }
                        onFileRemove={() => handleFileRemove(UPLOAD_IMAGE)}
                        value={formik.values[NAMES[UPLOAD_IMAGE]]} // Display current file
                        previewUrl={
                            data?.thumbnail
                                ? `${data?.thumbnail?.location}${data?.thumbnail?.thumbPath}`
                                : null
                        } // Existing thumbnail preview
                        previewSize={data?.thumbnail?.size}
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[UPLOAD_IMAGE]} />
                </Col>
            </Row>

            <Row className="my-4">
                <TextInput
                    placeholder={PLACEHOLDERS[SESSION_SUCCESS_MESSAGE]}
                    label={SESSION_SUCCESS_MESSAGE}
                    name={NAMES[SESSION_SUCCESS_MESSAGE]}
                    type="textarea"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values[NAMES[SESSION_SUCCESS_MESSAGE]]}
                    className="hide-default-icon"
                    disabled={isDisabled}
                />
                <FormikErrorText
                    formikInstance={formik}
                    fieldName={NAMES[SESSION_SUCCESS_MESSAGE]}
                />
            </Row>
            <Row className="my-4">
                <label className="input-label">{RADIO_GROUP}</label>

                <RadioGroup
                    field={NAMES[RADIO_GROUP]}
                    setFieldValue={formik.setFieldValue}
                    value={formik.values[NAMES[RADIO_GROUP]]}
                />

                <FormikErrorText formikInstance={formik} fieldName={NAMES[RADIO_GROUP]} />
            </Row>

            <div className="d-flex justify-content-end w-100 my-2 mt-4">
                <CustomButton
                    type="button"
                    title="Cancel"
                    className="me-3 custom-btn-sm"
                    color="primary"
                    outline
                    onClick={handleCancel}
                    disabled={isLoading}
                />
                <CustomButton
                    loading={isLoading}
                    type="submit"
                    title={mode === "add" ? "Add Audio" : "Update Audio"}
                    color="primary"
                    className="shadow-theme grad-primary"
                />
            </div>
        </form>
    );
};

export default AudioSoundscapeForm;
